import { useCallback, useEffect, useMemo, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';

import { NOTIFICATION_MESSAGES } from '@/shared/lib/constants';
import { glossaryKeys } from '@/shared/lib/query/queryKeys';
import type { DomainEditHook, DomainTreeNode } from '@/shared/types/editHooks';

import type { DomainHierarchyItem } from '@/entities/domain';
import { useDomainHierarchy } from '@/entities/domain';

import type { GlossaryTermDetail } from '@/features/glossary/api';
import { updateGlossaryTermDomains } from '@/features/glossary/api/updateGlossaryTermDomains';

const buildDomainTreeFromHierarchy = (items: DomainHierarchyItem[]): DomainTreeNode[] => {
  return items.map((item) => ({
    key: item.id,
    value: item.id,
    title: item.displayName || item.name,
    children: item.children && item.children.length > 0 ? buildDomainTreeFromHierarchy(item.children) : [],
    description: item.description,
    data: item,
  }));
};

type UseGlossaryTermDomainEditParams = {
  termId: string;
  termFqn: string;
  termCategory: string;
};

export const useGlossaryTermDomainEdit = ({
  termId,
  termFqn,
  termCategory,
}: UseGlossaryTermDomainEditParams): DomainEditHook => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDomainId, setSelectedDomainId] = useState<string | null>(null);

  const { data: hierarchyData, isLoading: isLoadingDomains } = useDomainHierarchy({ limit: 3000 });

  const queryKey = useMemo(() => ['glossaryTermDetail', termFqn] as const, [termFqn]);

  const termData = queryClient.getQueryData<GlossaryTermDetail>(queryKey);
  const currentDomains = useMemo(() => {
    const domains = termData?.domains || [];
    return domains.map((domain) => ({
      ...domain,
      description: domain.description || '',
      href: domain.fullyQualifiedName || '',
      tagFQN: domain.fullyQualifiedName || '',
    }));
  }, [termData?.domains]);
  const currentDomainId = useMemo(() => (currentDomains.length > 0 ? currentDomains[0]?.id : null), [currentDomains]);

  useEffect(() => {
    setSelectedDomainId(currentDomainId);
  }, [currentDomainId]);

  const updateDomainsMutation = useMutation({
    mutationFn: async (selectedDomainId: string | null) => {
      if (!hierarchyData?.data) {
        throw new Error(NOTIFICATION_MESSAGES.DOMAIN_TREE_ERROR);
      }
      await updateGlossaryTermDomains(termId, selectedDomainId, hierarchyData.data, currentDomains);
    },
    onError: (error) => {
      queryClient.invalidateQueries({ queryKey: glossaryKeys.termDetailByFqn(termFqn) });
      notification.error({
        message: NOTIFICATION_MESSAGES.DOMAIN_UPDATE_ERROR,
        description: error instanceof Error ? error.message : NOTIFICATION_MESSAGES.DOMAIN_UPDATE_ERROR_DESC,
        duration: 5,
        placement: 'bottomLeft',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: glossaryKeys.termDetailByFqn(termFqn) });
      queryClient.invalidateQueries({ queryKey: glossaryKeys.categoryInfo(termCategory) });
      notification.success({
        message: NOTIFICATION_MESSAGES.DOMAIN_UPDATE_SUCCESS,
        description: NOTIFICATION_MESSAGES.DOMAIN_UPDATE_SUCCESS_DESC,
        placement: 'bottomLeft',
        duration: 3,
      });
    },
  });

  const handleOpen = useCallback(() => {
    setIsOpen(true);
    const latestTermData = queryClient.getQueryData<GlossaryTermDetail>(queryKey);
    const latestDomains = latestTermData?.domains || [];
    const latestDomainId = latestDomains.length > 0 ? latestDomains[0]?.id : null;
    setSelectedDomainId(latestDomainId);
  }, [queryClient, queryKey]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    const latestTermData = queryClient.getQueryData<GlossaryTermDetail>(queryKey);
    const latestDomains = latestTermData?.domains || [];
    const latestDomainId = latestDomains.length > 0 ? latestDomains[0]?.id : null;
    setSelectedDomainId(latestDomainId);
  }, [queryClient, queryKey]);

  const handleSelect = useCallback(
    (keys: React.Key[]) => {
      const newKey = keys.length > 0 ? String(keys[0]) : null;

      if (newKey === selectedDomainId) {
        setSelectedDomainId(null);
      } else {
        setSelectedDomainId(newKey);
      }
    },
    [selectedDomainId]
  );

  const handleSave = useCallback(
    async (selectedDomainIds: string[]) => {
      const domainId = selectedDomainIds.length > 0 ? selectedDomainIds[0] : null;
      await updateDomainsMutation.mutateAsync(domainId);
    },
    [updateDomainsMutation]
  );

  const hasChanges = useMemo(() => {
    return currentDomainId !== selectedDomainId;
  }, [currentDomainId, selectedDomainId]);

  const treeData = useMemo(() => {
    if (!hierarchyData?.data) return [];
    return buildDomainTreeFromHierarchy(hierarchyData.data);
  }, [hierarchyData?.data]);

  return {
    isOpen,
    selectedDomainId,
    treeData,
    isLoadingDomains,
    isUpdating: updateDomainsMutation.isPending,
    hasChanges,
    handleOpen,
    handleClose,
    handleSelect,
    handleSave,
  };
};
